import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import WeatherTemperature from '../admin/EosDash/WeatherTemperature';
import PriceChart from '../admin/BuildingAutomation/MuiModularCard/PriceChart';
import EosConsumptionWidget from '../../components/EosConsumptionWidget';
import './PublicDisplay.scss';
import { isSameHour } from 'date-fns';
import Load from '../../atomicDesign/molecules/Load';
import { convertCurrency } from '../../utils/currencyConversion';
import { getExchangeRates } from '../../controller/buildingModule/buildings';
import MuiFaIcon from '../../atomicDesign/atoms/MuiFaIcon';

const PublicDisplay = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [weatherForecast, setWeatherForecast] = useState([]);
  const [electricPrices, setElectricPrices] = useState([]);
  const [consumption, setConsumption] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exchangeRates, setExchangeRates] = useState(null);

  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        const rates = await getExchangeRates();
        setExchangeRates(rates);
      } catch (err) {
        console.error('Error fetching exchange rates:', err);
      }
    };

    fetchExchangeRates();
  }, []);

  useEffect(() => {
    const authData = JSON.parse(
      localStorage.getItem('publicDisplayAuth') || 'null'
    );

    if (!authData || authData.token !== token || !authData.sessionToken) {
      navigate(`/public-display/${token}`);
      return;
    }

    const axiosWithAuth = axios.create({
      headers: {
        Authorization: `Bearer ${authData.sessionToken}`,
      },
    });

    const fetchAllData = async () => {
      try {
        setLoading(true);

        const response = await axiosWithAuth.post(
          '/api/v1/public-display/data'
        );

        if (response.data?.buildings && response.data.buildings.length > 0) {
          const firstBuilding = response.data.buildings[0];
          setBuildings(response.data.buildings);
          setSelectedBuilding(firstBuilding);

          const dashboardResponse = await axiosWithAuth.post(
            `/api/v1/public-display/dashboard/${firstBuilding.buildingId}`
          );

          setWeatherForecast(dashboardResponse.data?.weather ?? []);
          setElectricPrices(dashboardResponse.data?.prices ?? []);
          setConsumption(dashboardResponse.data?.consumption ?? {});
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err.response?.data || 'Failed to load data');

        if (err.response?.status === 401) {
          localStorage.removeItem('publicDisplayAuth');
          navigate(`/public-display/${token}`);
        }

        setWeatherForecast([]);
        setElectricPrices([]);
        setConsumption({});
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
    const interval = setInterval(fetchAllData, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [token, navigate]);

  const rates = exchangeRates?.data?.rates || {};
  const hasNokRate = !!rates?.NOK;
  const displayCurrency = hasNokRate ? 'NOK' : 'EUR';

  let currentTemperature = null;
  let avgTemp = null;
  let tempDirection = 'down';

  if (weatherForecast?.length > 0) {
    const tempValues = weatherForecast
      .map((item) => parseFloat(item.air_temperature))
      .filter((temp) => !isNaN(temp));

    avgTemp =
      tempValues.length > 0
        ? tempValues.reduce((sum, temp) => sum + temp, 0) / tempValues.length
        : null;

    const sortedData = [...weatherForecast].sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );

    const now = new Date();
    const currentHourData = sortedData.find((entry) =>
      isSameHour(new Date(entry.timestamp), now)
    );

    if (currentHourData?.air_temperature) {
      currentTemperature = parseFloat(currentHourData.air_temperature);
    } else if (sortedData?.[0]?.air_temperature) {
      currentTemperature = parseFloat(sortedData[0].air_temperature);
    }

    if (!isNaN(currentTemperature) && !isNaN(avgTemp)) {
      tempDirection = currentTemperature > avgTemp ? 'up' : 'down';
    }
  }

  let currentPrice = null;
  let avgPrice = null;
  let priceDirection = 'down';

  if (electricPrices?.length > 0) {
    let flatData = [];
    const now = new Date();
    const twoHoursAgo = new Date(now - 1000 * 60 * 60 * 2);

    for (let day of electricPrices) {
      if (day?.data?.length > 0) {
        for (let hour of day.data) {
          const thisDateTime = new Date(hour.time_start);
          if (thisDateTime < twoHoursAgo) continue;

          const convertedPrice = convertCurrency(
            parseFloat(hour.EUR_per_kWh),
            displayCurrency,
            rates || {}
          );

          flatData.push({
            time_start: hour.time_start,
            EUR_per_kWh: hour.EUR_per_kWh,
            price_per_kWh: convertedPrice,
          });
        }
      }
    }

    const prices = flatData
      .map((item) => parseFloat(item.price_per_kWh))
      .filter((price) => !isNaN(price));

    avgPrice =
      prices.length > 0
        ? prices.reduce((sum, price) => sum + price, 0) / prices.length
        : null;

    const currentHourData = flatData.find((entry) =>
      isSameHour(new Date(entry.time_start), now)
    );

    if (currentHourData?.price_per_kWh != null) {
      currentPrice = parseFloat(currentHourData.price_per_kWh);
    } else if (prices.length > 0) {
      currentPrice = prices[0];
    }

    if (!isNaN(currentPrice) && !isNaN(avgPrice)) {
      priceDirection = currentPrice > avgPrice ? 'up' : 'down';
    }
  }

  const currentCo2 = consumption?.co2Emissions ?? null;
  const baselineCo2 = consumption?.baselineCo2 ?? null;
  const co2Direction =
    currentCo2 != null && baselineCo2 != null && currentCo2 > baselineCo2
      ? 'up'
      : 'down';

  if (loading) {
    return (
      <div className="public-display-container">
        <div className="eos-dash-container">
          <Load />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-display-container">
        <div className="eos-dash-container">
          <div className="error-message">
            <MuiFaIcon name="fas fa-exclamation-triangle" />
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-display-container">
      <div className="eos-dash-container public-display">
        <div className="dashboard-content">
          {selectedBuilding && (
            <>
              <div className="stats-overview">
                <div className="stat-card dashboard-card">
                  <div className="stat-icon">
                    <MuiFaIcon name="fas fa-building" />
                  </div>
                  <div className="stat-content">
                    <div className="stat-title">Building</div>
                    <div className="stat-value">{selectedBuilding.name}</div>
                  </div>
                </div>

                <div className="stat-card dashboard-card">
                  <div className="stat-icon">
                    <MuiFaIcon name="fas fa-temperature-high" />
                  </div>
                  <div className="stat-content">
                    <div className="stat-title">Outdoor Temperature</div>
                    <div className="stat-value-wrapper">
                      {currentTemperature !== null &&
                      !isNaN(currentTemperature) ? (
                        <>
                          <div className={`stat-value ${tempDirection}`}>
                            {currentTemperature.toFixed(1)}°C
                          </div>
                          <div className={`stat-trend ${tempDirection}`}>
                            {tempDirection === 'up' ? '↑' : '↓'}
                          </div>
                        </>
                      ) : (
                        <div className="stat-value">N/A</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="stat-card dashboard-card">
                  <div className="stat-icon">
                    <MuiFaIcon name="fas fa-bolt" />
                  </div>
                  <div className="stat-content">
                    <div className="stat-title">Electricity Prices</div>
                    <div className="stat-value-wrapper">
                      {currentPrice !== null && !isNaN(currentPrice) ? (
                        <>
                          <div className={`stat-value ${priceDirection}`}>
                            {displayCurrency === 'NOK'
                              ? currentPrice < 1
                                ? `${(currentPrice * 100).toFixed(0)} øre/kWh`
                                : `${currentPrice.toFixed(2)} kr/kWh`
                              : currentPrice < 1
                                ? `${(currentPrice * 100).toFixed(1)} €/kWh`
                                : `${currentPrice.toFixed(3)} €/kWh`}
                          </div>
                          <div className={`stat-trend ${priceDirection}`}>
                            {priceDirection === 'up' ? '↑' : '↓'}
                          </div>
                        </>
                      ) : (
                        <div className="stat-value">N/A</div>
                      )}
                    </div>
                  </div>
                </div>

                {currentCo2 !== null && (
                  <div className="stat-card dashboard-card">
                    <div className="stat-icon">
                      <MuiFaIcon name="fas fa-leaf" />
                    </div>
                    <div className="stat-content">
                      <div className="stat-title">Production CO2</div>
                      <div className="stat-value-wrapper">
                        <div className={`stat-value ${co2Direction}`}>
                          {currentCo2 > 1000 ? (
                            <>
                              {(currentCo2 / 1000).toFixed(1)} kg CO<sub>2</sub>
                            </>
                          ) : (
                            <>
                              {currentCo2.toFixed(0)} g CO<sub>2</sub>
                            </>
                          )}
                        </div>
                        {baselineCo2 !== null && (
                          <div className={`stat-trend ${co2Direction}`}>
                            {co2Direction === 'up' ? '↑' : '↓'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="stat-card dashboard-card">
                  <div className="stat-icon">
                    <MuiFaIcon name="fas fa-bolt" />
                  </div>
                  <div
                    className="stat-content"
                    title="Yesterday's consumption, compared to baseline in parentheses"
                  >
                    <EosConsumptionWidget
                      buildingId={selectedBuilding.buildingId}
                      consumption={consumption}
                    />
                  </div>
                </div>
              </div>

              <div className="chart-section__container">
                <div className="dashboard-card">
                  <div className="chart-section">
                    <div key={`weather-${selectedBuilding?.buildingId}`}>
                      {!weatherForecast || weatherForecast.length === 0 ? (
                        <div className="no-data-message">
                          <MuiFaIcon name="fas fa-exclamation-circle" />
                          <p>No weather data available.</p>
                        </div>
                      ) : (
                        <WeatherTemperature
                          data={weatherForecast.map((item) => ({
                            ...item,
                            air_temperature: parseFloat(item.air_temperature),
                          }))}
                          hideCurrentTemp={true}
                          height={300}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="dashboard-card">
                  <div className="chart-section">
                    <div key={`prices-${selectedBuilding?.buildingId}`}>
                      {!electricPrices || electricPrices.length === 0 ? (
                        <div className="no-data-message">
                          <MuiFaIcon name="fas fa-exclamation-circle" />
                          <p>No price data available.</p>
                        </div>
                      ) : (
                        <PriceChart
                          data={electricPrices}
                          height={300}
                          hideCurrentPrice={true}
                          currency={displayCurrency}
                          exchangeRates={rates}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="public-display-footer">
            <p>Public Energy Dashboard - Updated Every 5 Minutes</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicDisplay;
